import type { WorldConfig } from '../../config/world.config';
import type { GreyboxMaterialId, GreyboxPart } from '../AssetTypes';
import { GreyboxMaterialId as M } from '../AssetTypes';
import { box, cylinder, groundChevron, straightSegmentGreybox } from './greybox';

/**
 * Greyboxes of the terrain modules (level variety phase 1): pits, scaffold passage,
 * pipe passage, ramp – plus their set-piece obstacles. Dimensions come from the
 * segment definitions, so gameplay geometry and greybox never drift apart.
 * The 40 m modules are overlays: their road comes from two SEG_STRAIGHT_20M visuals.
 */

/** Rectangle on a module's floor plan (segment-local, meters). */
export interface FloorRect {
  readonly zFrom: number;
  readonly zTo: number;
  readonly xFrom: number;
  readonly xTo: number;
}

const RAD_TO_DEG = 180 / Math.PI;

/** Cylinder lying along X (across the track), centered at the given position. */
export function pipeAlongX(diameter: number, length: number, x: number, y: number, z: number, material: GreyboxMaterialId): GreyboxPart {
  return { shape: 'cylinder', size: [diameter, length, diameter], position: [x, y, z], rotation: [0, 0, 90], material };
}

/** Road slab pieces covering the track except the holes (z bands × free x intervals). */
function roadAround(world: WorldConfig, length: number, holes: readonly FloorRect[]): GreyboxPart[] {
  const half = world.trackWidth / 2;
  const cuts = [...new Set([0, length, ...holes.flatMap((h) => [h.zFrom, h.zTo])])].filter((z) => z >= 0 && z <= length).sort((a, b) => a - b);
  const parts: GreyboxPart[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const z0 = cuts[i] as number;
    const z1 = cuts[i + 1] as number;
    const inBand = holes.filter((h) => h.zFrom < z1 && h.zTo > z0).sort((a, b) => a.xFrom - b.xFrom);
    let x = -half;
    for (const hole of [...inBand, { xFrom: half, xTo: half }]) {
      if (hole.xFrom > x + 1e-6) parts.push(box([hole.xFrom - x, 0.2, z1 - z0], (x + hole.xFrom) / 2, -0.2, (z0 + z1) / 2, M.Track));
      x = Math.max(x, hole.xTo);
    }
  }
  return parts;
}

/**
 * Straight module with pits cut into the road (SEG_04): pit walls and floor, hazard
 * stripes on the rims you jump from and onto, barrier tape on posts along the long
 * sides (visual only).
 */
export function pitSegmentGreybox(world: WorldConfig, length: number, pits: readonly (FloorRect & { readonly depth: number })[]): GreyboxPart[] {
  const parts = straightSegmentGreybox(world, length).filter((part) => part.material !== M.Track && part.material !== M.LaneLine);
  parts.push(...roadAround(world, length, pits));
  const half = world.trackWidth / 2;
  for (let lane = 1; lane < world.laneCount; lane++) {
    const x = -half + lane * world.laneWidth;
    const crossing = pits.filter((p) => x >= p.xFrom - 0.05 && x <= p.xTo + 0.05).sort((a, b) => a.zFrom - b.zFrom);
    let z = 0;
    for (const pit of [...crossing, { zFrom: length, zTo: length }]) {
      if (pit.zFrom > z) parts.push(box([0.08, 0.01, pit.zFrom - z], x, 0.002, (z + pit.zFrom) / 2, M.LaneLine));
      z = Math.max(z, pit.zTo);
    }
  }
  const wall = 0.15;
  const stripe = 0.25;
  const postHeight = 1;
  for (const pit of pits) {
    const w = pit.xTo - pit.xFrom;
    const d = pit.zTo - pit.zFrom;
    const cx = (pit.xFrom + pit.xTo) / 2;
    const cz = (pit.zFrom + pit.zTo) / 2;
    parts.push(
      box([w, 0.1, d], cx, -pit.depth, cz, M.TrackSide),
      box([wall, pit.depth, d], pit.xFrom + wall / 2, -pit.depth, cz, M.Concrete),
      box([wall, pit.depth, d], pit.xTo - wall / 2, -pit.depth, cz, M.Concrete),
      box([w, pit.depth, wall], cx, -pit.depth, pit.zFrom + wall / 2, M.Concrete),
      box([w, pit.depth, wall], cx, -pit.depth, pit.zTo - wall / 2, M.Concrete),
      // hazard stripes on the road at the take-off and landing rims
      box([w, 0.02, stripe], cx, 0, pit.zFrom - stripe / 2, M.Chevron),
      box([w, 0.02, stripe], cx, 0, pit.zTo + stripe / 2, M.Chevron),
    );
    for (const x of [pit.xFrom, pit.xTo]) {
      for (const z of [pit.zFrom, pit.zTo]) parts.push(cylinder(0.06, postHeight, x, 0, z, M.Barrier));
      parts.push(box([0.02, 0.08, d], x, postHeight - 0.12, cz, M.Barrier));
    }
  }
  return parts;
}

export interface ScaffoldGreyboxSpec {
  readonly deck: { readonly zFrom: number; readonly zTo: number; readonly clearance: number };
  readonly bar: { readonly clearance: number; readonly depth: number };
  readonly bars: readonly number[];
  readonly halfSpan: number;
}

/**
 * SEG_06 scaffold passage: standards beside the barriers, plank deck with orange toe
 * boards, striped crossbars with ledgers above (no gap a runner fits through), braces.
 */
export function scaffoldPassageGreybox(spec: ScaffoldGreyboxSpec): GreyboxPart[] {
  const standardX = 3.95;
  const tube = 0.06;
  const deckThickness = 0.06;
  const rail = 1;
  const ledgerY = 2.7;
  const bay = 2;
  const { deck, bar } = spec;
  const width = 2 * standardX + 0.3;
  const length = deck.zTo - deck.zFrom;
  const mid = (deck.zFrom + deck.zTo) / 2;
  const parts: GreyboxPart[] = [
    box([width, deckThickness, length], 0, deck.clearance, mid, M.Wood),
    box([width, 0.2, 0.04], 0, deck.clearance + deckThickness, deck.zFrom, M.Container),
    box([width, 0.2, 0.04], 0, deck.clearance + deckThickness, deck.zTo, M.Container),
  ];
  for (const side of [-1, 1] as const) {
    parts.push(
      box([0.04, 0.2, length], side * (standardX + 0.15), deck.clearance + deckThickness, mid, M.Container),
      box([tube, tube, length], side * standardX, deck.clearance + rail, mid, M.Steel),
      box([tube, tube, length], side * standardX, ledgerY, mid, M.Steel),
    );
    for (let z = deck.zFrom; z <= deck.zTo + 1e-6; z += bay) {
      parts.push(box([tube, deck.clearance + rail, tube], side * standardX, 0, z, M.Steel));
    }
    // diagonal braces outside the standards (one per two bays)
    for (let z = deck.zFrom; z + 2 * bay <= deck.zTo + 1e-6; z += 2 * bay) {
      parts.push({
        shape: 'box',
        size: [tube, Math.hypot(2 * bay, deck.clearance), tube],
        position: [side * (standardX + 0.08), deck.clearance / 2, z + bay],
        rotation: [Math.atan2(2 * bay, deck.clearance) * RAD_TO_DEG, 0, 0],
        material: M.Steel,
      });
    }
  }
  for (const z of spec.bars) {
    parts.push(
      box([2 * spec.halfSpan, 0.22, bar.depth], 0, bar.clearance, z, M.Chevron),
      box([2 * spec.halfSpan, 0.08, 0.08], 0, (bar.clearance + ledgerY) / 2 + 0.1, z, M.Steel),
      box([2 * spec.halfSpan, 0.08, 0.08], 0, ledgerY, z, M.Steel),
    );
  }
  return parts;
}

export interface PipeGreyboxSpec {
  readonly pipes: readonly { readonly x: number; readonly zFrom: number; readonly zTo: number }[];
  readonly innerRadius: number;
  readonly wall: number;
  readonly bridges: readonly { readonly z: number; readonly xFrom: number; readonly xTo: number; readonly postX: number }[];
  readonly bridgeClearance: number;
  readonly bridgeDiameter: number;
}

/** Ring of box segments around an axis along z (open at the bottom where it meets the road). */
function pipeRing(x: number, axisY: number, radius: number, thickness: number, z: number, length: number, extraChord = 0): GreyboxPart[] {
  const segments = 12;
  const chord = 2 * radius * Math.sin(Math.PI / segments) + 0.04 + extraChord;
  const parts: GreyboxPart[] = [];
  for (let i = 0; i < segments; i++) {
    const theta = (i / segments) * 2 * Math.PI; // 0 = top, clockwise seen from behind
    const y = axisY + radius * Math.cos(theta);
    if (y < 0.15) continue; // below the road
    parts.push({
      shape: 'box',
      size: [chord, thickness, length],
      position: [x + radius * Math.sin(theta), y, z],
      rotation: [0, 0, -theta * RAD_TO_DEG],
      material: M.Concrete,
    });
  }
  return parts;
}

/**
 * SEG_07 pipe passage: walk-in concrete pipes standing on the road (their floor is the
 * road) with socket rings at the joints, and pipe racks crossing two lanes (slide under
 * the lower pipe; posts at both ends, one between the lanes).
 */
export function pipePassageGreybox(spec: PipeGreyboxSpec): GreyboxPart[] {
  const parts: GreyboxPart[] = [];
  const radius = spec.innerRadius + spec.wall / 2;
  const socket = { offset: 0.08, length: 0.3 };
  for (const pipe of spec.pipes) {
    const length = pipe.zTo - pipe.zFrom;
    parts.push(...pipeRing(pipe.x, spec.innerRadius, radius, spec.wall, (pipe.zFrom + pipe.zTo) / 2, length));
    for (const end of [pipe.zFrom + socket.length / 2, pipe.zTo - socket.length / 2]) {
      parts.push(...pipeRing(pipe.x, spec.innerRadius, radius + socket.offset, spec.wall, end, socket.length, 0.05));
    }
  }
  const rackTop = 3.4;
  for (const bridge of spec.bridges) {
    const span = bridge.xTo - bridge.xFrom + 0.6;
    const cx = (bridge.xFrom + bridge.xTo) / 2;
    const d = spec.bridgeDiameter;
    parts.push(
      pipeAlongX(d, span, cx, spec.bridgeClearance + d / 2, bridge.z, M.Concrete),
      pipeAlongX(d * 0.7, span, cx, spec.bridgeClearance + d + 0.35, bridge.z, M.Steel),
      box([span, 0.15, 0.3], cx, rackTop, bridge.z, M.Container),
    );
    const outerX = bridge.postX > cx ? bridge.xFrom - 0.25 : bridge.xTo + 0.25;
    for (const x of [bridge.postX, outerX]) parts.push(box([0.2, rackTop, 0.3], x, 0, bridge.z, M.Container));
  }
  return parts;
}

export interface RampGreyboxSpec {
  readonly xFrom: number;
  readonly xTo: number;
  readonly ramp: { readonly zFrom: number; readonly zTo: number };
  readonly platform: { readonly zFrom: number; readonly zTo: number };
  readonly height: number;
}

/**
 * SEG_08 ramp and raised platform: sloped plank ramp on a stepped timber crib,
 * concrete platform with plank top, hazard stripes on its edges, ground chevrons
 * pointing up the ramp.
 */
export function rampGreybox(spec: RampGreyboxSpec): GreyboxPart[] {
  const width = spec.xTo - spec.xFrom;
  const cx = (spec.xFrom + spec.xTo) / 2;
  const rampLength = spec.ramp.zTo - spec.ramp.zFrom;
  const plate = 0.12;
  const top = 0.05;
  const slice = 1;
  const slopeDegrees = Math.atan2(spec.height, rampLength) * RAD_TO_DEG;
  const parts: GreyboxPart[] = [
    {
      shape: 'box',
      size: [width, plate, Math.hypot(rampLength, spec.height)],
      position: [cx, spec.height / 2 - plate / 2, (spec.ramp.zFrom + spec.ramp.zTo) / 2],
      rotation: [-slopeDegrees, 0, 0], // front end up (Babylon: +x rotation tilts +z down)
      material: M.Wood,
    },
  ];
  // timber crib under the ramp: slices up to the plate
  for (let z = spec.ramp.zFrom; z + slice <= spec.ramp.zTo + 1e-6; z += slice) {
    const h = (spec.height * (z - spec.ramp.zFrom)) / rampLength - plate;
    if (h > 0.05) parts.push(box([width - 0.1, h, slice * 0.9], cx, 0, z + slice / 2, M.Wood));
  }
  const platformLength = spec.platform.zTo - spec.platform.zFrom;
  const pz = (spec.platform.zFrom + spec.platform.zTo) / 2;
  parts.push(
    box([width, spec.height - top, platformLength], cx, 0, pz, M.Concrete),
    box([width, top, platformLength], cx, spec.height - top, pz, M.Wood),
    // hazard stripes: drop edge and side edges
    box([width, 0.3, 0.04], cx, spec.height - 0.32, spec.platform.zTo + 0.02, M.Chevron),
    box([0.04, 0.2, platformLength], spec.xFrom - 0.02, spec.height - 0.22, pz, M.Chevron),
    box([0.04, 0.2, platformLength], spec.xTo + 0.02, spec.height - 0.22, pz, M.Chevron),
    ...groundChevron(cx, spec.ramp.zFrom - 0.6, 0),
    ...groundChevron(cx, spec.ramp.zFrom - 2.2, 0),
  );
  return parts;
}

/** OBS_MATERIAL_STACK_A: pallets and concrete blocks stacked up to a 1.6 m crossbar. */
export function materialStackGreybox(): GreyboxPart[] {
  return [
    box([1.6, 0.14, 1.2], 0, 0, 0, M.Wood),
    box([1.5, 0.6, 1.1], 0, 0.14, 0, M.Concrete),
    box([1.6, 0.14, 1.2], 0, 0.74, 0, M.Wood),
    box([1.45, 0.58, 1.05], 0, 0.88, 0, M.Concrete),
    box([1.6, 0.14, 1.2], 0, 1.46, 0, M.Wood),
  ];
}

/** OBS_CABLE_DRUM_A: small cable drum Ø 0.9 m, axis across the lane. */
export function cableDrumGreybox(): GreyboxPart[] {
  return [
    pipeAlongX(0.9, 0.12, -0.45, 0.45, 0, M.Wood),
    pipeAlongX(0.9, 0.12, 0.45, 0.45, 0, M.Wood),
    pipeAlongX(0.62, 0.8, 0, 0.45, 0, M.Steel),
  ];
}
